// University of Alabama at Birmingham: public college page (#2839), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UAB's Common Data Set 2025-2026 (Institutional Effectiveness and
//    Analysis; the newest edition on its Common Data Set page): C1
//    applicants, admits and enrollees for Fall 2025 with the residency table
//    (the Unknown column is 0 and left out), C8 test policy for Fall 2027,
//    C14 (no closing date, a June 1 priority date), C16 (rolling
//    notification), C21/C22 (no early decision or early action plan), G1/G5
//    2026-2027 costs. Its C8A answers "No" to using SAT or ACT scores while
//    checking "not required for admission, but considered if submitted";
//    UAB's admissions pages are used for the policy.
//  - UAB Undergraduate Admissions: Freshman How to Apply (the Fall 2027
//    priority and scholarship deadlines), Test-Optional Admission Policy
//    (test-optional for Summer or Fall 2027 only with a 3.5+ weighted GPA;
//    test-optional ends after the Summer/Fall 2027 cycle, so fall 2028
//    applicants are expected to send scores) and Freshman Admission
//    Requirements. No page publishes decision dates.
//  - College Scorecard API, school id 100663 (net price by family income,
//    in-state).
//  - UAB Undergraduate Admissions, Estimated Costs
//    (www.uab.edu/admissions/cost/estimated-costs, 2026-2027 cost of
//    attendance tables), for the comparison below only.
// Cost: the CDS G1/G5 items are used, with the one G5 transportation figure
// in both columns. Departure: UAB's Estimated Costs page (2026-2027,
// on-campus freshmen) builds a different budget: tuition and fees of $11,970
// in-state and $29,820 out-of-state (the CDS tuition alone; the CDS adds
// $500 in required fees), a $5,730 meal plan (CDS food $5,280), $3,146 in
// health insurance, and $2,979 out-of-state transportation, for totals of
// $36,333 and $54,541.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UAB Common Data Set 2025-2026',
        url: 'https://www.uab.edu/institutionaleffectiveness/images/documents/cds/CDS-2025-26.pdf',
        read: '2026-10-01',
    },
    howToApply: {
        label: 'UAB Undergraduate Admissions, Freshman How to Apply',
        url: 'https://www.uab.edu/admissions/apply/freshman/how-to-apply',
        read: '2026-10-01',
    },
    testOptional: {
        label: 'UAB Undergraduate Admissions, Test-Optional Admission Policy',
        url: 'https://www.uab.edu/admissions/apply/test-optional',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?100663-University-of-Alabama-at-Birmingham',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-alabama-at-birmingham',
    kbId: 'university_of_alabama_at_birmingham',
    name: 'University of Alabama at Birmingham',
    shortName: 'UAB',
    aliases: ['UAB'],
    location: 'Birmingham, AL',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UAB brand colors); decoration only.
    branding: {
        primary: '#1A5632',
        secondary: '#FDB913',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_alabama_at_birmingham_logo.png',
    },
    faq: {
        answer: { text: 'The University of Alabama at Birmingham admitted 94.2% of first-year applicants for fall 2025, 10,474 of the 11,119 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'What is the UAB acceptance rate?', a: 'For fall 2025 entry, UAB admitted 10,474 of 11,119 first-year applicants, an acceptance rate of 94.2%, and 2,519 enrolled.', source: 'cds', where: 'section C1' },
            { q: 'What is the UAB acceptance rate for out-of-state students?', a: 'For fall 2025, UAB admitted 2,949 of 3,129 out-of-state first-year applicants, 94.2%.', source: 'cds', where: 'section C1, residency table' },
            { q: 'What is the UAB application deadline for fall 2027?', a: 'The priority deadline for admission is June 1. For merit scholarships, apply by January 15, 2027 if you live in Alabama or by May 1, 2027 if you live out of state. If you miss June 1, UAB still considers you as space and funds allow. The application fee is $40.', source: 'howToApply' },
            { q: 'Does UAB have rolling admissions?', a: 'Yes. UAB has no closing date and no early action or early decision plan, and its Common Data Set says it sends decisions on a rolling basis.', source: 'cds', where: 'sections C14, C16, C21 and C22' },
            { q: 'How much does UAB cost?', a: 'For 2026-2027, the Common Data Set figures for a full-time undergraduate living on campus add up to $34,197 for Alabama residents and $52,047 for out-of-state students, with tuition of $11,970 and $29,820. Some programs charge more.', source: 'cds', where: 'sections G1, G4 and G5' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 11119,
        admitted: 10474,
        enrolled: 2519,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 6974, admitted: 6574 },
            { group: 'Out-of-state', applied: 3129, admitted: 2949 },
            { group: 'International', applied: 1016, admitted: 951 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UAB\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'UAB has no early decision or early action plan. It reviews applications on a rolling basis, and its June 1 date is a priority deadline, not a closing date.',
        source: 'cds',
        where: 'sections C14, C16, C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'June 1' },
        rows: [
            { plan: 'Scholarship deadline, in-state', deadline: 'January 15', decision: null, binding: false },
            { plan: 'Scholarship deadline, out-of-state', deadline: 'May 1', decision: null, binding: false },
            { plan: 'Priority deadline for admission', deadline: 'June 1', decision: null, binding: false },
        ],
        note: 'To be considered for merit scholarships, apply by January 15, 2027 if you live in Alabama and by May 1, 2027 if you live out of state; no separate scholarship application is needed. Students who miss the June 1 priority deadline are still considered as space and funds allow. You can apply with the UAB Application or the Common Application; the fee is $40. UAB does not publish decision dates; its Common Data Set says it sends decisions on a rolling basis.',
        source: 'howToApply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'For fall 2027, UAB does not require the ACT or SAT for admission or merit scholarships if you have a weighted high school GPA of 3.5 or higher; you choose on the application whether to apply with a score or test-optional, and the choice is final. With a weighted GPA of 3.49 or below, you must send a score. If you apply with a score, UAB recommends a composite of at least 20 on the ACT or 1030 on the SAT. The early medical, dental and optometry school acceptance programs require scores.',
        source: 'testOptional',
        cdsLine: 'The Common Data Set marks the SAT or ACT as not required for admission but considered if submitted for fall 2027 applicants; it does not mention the GPA condition.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            {
                topic: 'Testing',
                line: 'UAB ends test-optional admission after the Summer/Fall 2027 cycle. Students applying for fall 2028 or later are expected to send SAT or ACT scores for admission and scholarship review.',
                source: 'testOptional',
            },
        ],
        source: 'testOptional',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [11970, 29820] },
            { item: 'Required fees', amounts: [500, 500] },
            { item: 'Food and housing (on campus)', amounts: [14720, 14720] },
            { item: 'Books and supplies', amounts: [1200, 1200] },
            { item: 'Transportation', amounts: [2621, 2621] },
            { item: 'Other expenses', amounts: [3186, 3186] },
        ],
        totals: [34197, 52047],
        note: 'For a full-time undergraduate living on campus, fall and spring. Some programs charge more.',
        source: 'cds',
        where: 'sections G1, G4 and G5',
        netPrice: {
            average: 15630,
            byIncome: [
                { income: '$0 to $30,000', amount: 13073 },
                { income: '$30,001 to $48,000', amount: 12599 },
                { income: '$48,001 to $75,000', amount: 13912 },
                { income: '$75,001 to $110,000', amount: 18789 },
                { income: 'Over $110,000', amount: 21893 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UAB\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
